import { memo, useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { TouchableButton, TouchableDiv } from '../../common/Touchable';
import styles from './headerButtons.module.scss';
import cn from 'classnames';
import { ReactComponent as Hamburger } from '@assets/images/hamburger.svg';
import HamburgerMenu from './hamburgerMenu';
import { ReactComponent as BellIcon } from '@assets/images/bell-icon.svg';

const ShowNotificationModal = lazy(() => import('../Modals/Notification/ShowNotification'));
const OrderMenuTop = lazy(() => import('../../OrderMenuTopV2'));

const NOTIFICATION_INITIAL = { state: false, count: 0 };

// Isolated so only this re-renders when paid status changes — bell and HamburgerMenu are unaffected.
const HeaderMiddleSection = memo(({
  isOnlyOrderMode,
  headerButtons,
  serverAppTheme,
  orderingPortraitOrientation,
  onClickHandler,
}) => {
  const isPaid = useSelector(s => isOnlyOrderMode ? s.orders.seats?.open === false : false);

  if (isOnlyOrderMode && !isPaid) {
    return (
      <Suspense fallback={null}>
        <OrderMenuTop isOnlyOrderMode={isOnlyOrderMode} />
      </Suspense>
    );
  }
  if (isOnlyOrderMode) return null;
  return (
    <div className="w-80 d-flex overflow-auto align-items-center">
      {headerButtons?.map(
        button =>
          button.supportOpen && (
            <TouchableButton
              key={button.key}
              onClick={onClickHandler[button.key]}
              className={cn(
                styles.menuItem,
                orderingPortraitOrientation
                  ? styles.minWidthPortrait
                  : styles.minWidthLand,
                'd-flex justify-content-center align-items-center px-2 btn35 w-100 text-center mr-1'
              )}
              style={{
                background: serverAppTheme?.secondaryBtn,
                color: serverAppTheme?.btn_font_color,
              }}
              role="button"
              tabIndex="0"
            >
              {button.name}
            </TouchableButton>
          )
      )}
    </div>
  );
});

const HeaderCheckButtons = ({
  headerButtons = [],
  serverAppTheme = {},
  orderingPortraitOrientation = false,
  onOpenTable,
  onOpenTab,
  thirdPartyAppName,
  displayName,
  jobName,
  allChecksVisible,
  getAllChecks,
  storeId,
  removeSeat,
  setVisible,
  showOpenChecks,
  onOpenTogo,
  onOpenSpeedOrder,
  onOpenCounter,
  isLandScape,
  notificationTableData = [],
  notifyLoading,
  removeStoreNotification,
  acknowledge,
  isOnlyOrderMode,
}) => {
  const onClickHandler = useMemo(
    () => ({
      table: onOpenTable,
      tab: onOpenTab,
      togo: onOpenTogo,
      counter: onOpenCounter || (() => {}),
      speedOrder: onOpenSpeedOrder || (() => {}),
    }),
    [onOpenTable, onOpenTab, onOpenTogo, onOpenCounter, onOpenSpeedOrder]
  );
  const [toggleHamburgerMenu, setToggleHamburgerMenu] = useState(false);
  const [showNotification, setShowNotification] = useState(NOTIFICATION_INITIAL);

  const handleToggleHamburgerMenu = useCallback(() => setToggleHamburgerMenu(prev => !prev), []);

  const handleOpenNotification = useCallback(() => {
    setShowNotification(prev => ({ ...prev, state: true }));
  }, []);

  useEffect(() => {
    const count = notificationTableData?.length ?? 0;
    setShowNotification(prev => (prev.count === count ? prev : { ...prev, count }));
  }, [notificationTableData]);

  return (
    <div className="w-100 d-flex position-relative">
      <div className="w-10 h-100 d-flex justify-content-center align-items-center">
        <TouchableButton
          className="d-flex justify-content-center align-items-center"
          onClick={e => {
            e.preventDefault();
            handleToggleHamburgerMenu();
          }}
          style={{ border: 'none', background: 'none' }}
        >
          <Hamburger
            fill={serverAppTheme?.secondary_iconcolor}
            alt="appsIcon"
          />
        </TouchableButton>
      </div>

      <HeaderMiddleSection
        isOnlyOrderMode={isOnlyOrderMode}
        headerButtons={headerButtons}
        serverAppTheme={serverAppTheme}
        orderingPortraitOrientation={orderingPortraitOrientation}
        onClickHandler={onClickHandler}
      />

      <div className="w-10 h-100 pr-2 d-flex justify-content-end align-items-center">
        <div
          className={cn(
            styles.notificationIcon,
            'w-10 d-flex justify-content-center align-items-center px-0 mr-0 rounded position-relative'
          )}
          style={{ background: 'rgb(230, 230, 230)' }}
        >
          <TouchableDiv onClick={handleOpenNotification}>
            <BellIcon
              fill={serverAppTheme?.iconcolor}
              className="d-flex px-0 d-flex justify-content-center align-items-center"
            />
          </TouchableDiv>
          {showNotification.count > 0 && (
            <div
              className={cn('bg-primary position-absolute fs_10 text-center')}
              style={{
                top: 0,
                right: 0,
                minWidth: '16px',
                borderRadius: '8px',
                padding: '0 3px',
              }}
            >
              <span className="fw-600 text-white">
                {showNotification.count}
              </span>
            </div>
          )}
        </div>
      </div>

      {toggleHamburgerMenu && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          onClick={handleToggleHamburgerMenu}
        />
      )}
      {toggleHamburgerMenu && (
        <HamburgerMenu
          orderingPortraitOrientation={orderingPortraitOrientation}
          isOpen={toggleHamburgerMenu}
          handleToggleHamburgerMenu={handleToggleHamburgerMenu}
          serverAppTheme={serverAppTheme}
          thirdPartyAppName={thirdPartyAppName}
          displayName={displayName}
          jobName={jobName}
          allChecksVisible={allChecksVisible}
          getAllChecks={getAllChecks}
          storeId={storeId}
          removeSeat={removeSeat}
          setVisible={setVisible}
          showOpenChecks={showOpenChecks}
          onOpenTab={onOpenTab}
        />
      )}

      {showNotification.state && (
        <Suspense fallback={null}>
          <ShowNotificationModal
            showNotification={showNotification}
            setShowNotification={setShowNotification}
            serverAppTheme={serverAppTheme}
            isLandScape={isLandScape}
            notificationTableData={notificationTableData}
            notifyLoading={notifyLoading}
            removeStoreNotification={removeStoreNotification}
            acknowledge={acknowledge}
          />
        </Suspense>
      )}
    </div>
  );
};

export default memo(HeaderCheckButtons);
